import { Button } from '@mui/material';
import { FaFloppyDisk, FaTrash, FaXmark } from 'react-icons/fa6';
import { Link, useNavigate } from 'react-router-dom';
import { notify } from '~/components/Notifications';
import { reactQueryClient } from '~/shared/contexts/CommonAppContext';
import { Chart, CreateableChart, UpdatableChart } from '~/shared/types/chart';
import { useCreateChart, useDestroyChart, useUpdateChart } from '~/sites/music/api/charts_queries';
import { chartEditPath, chartShowPath } from '~/sites/music/routes/paths';
import { useChartEditContext } from './ChartEditContext';

type SubmitAction = 'save' | 'delete' | 'duplicate'

const getUpdatableChart = (chart: Chart): UpdatableChart => {
  return (
    {
      song_title: chart.song_title,
      notes: chart.notes,
      chords: chart.chords,
      lyrics: chart.lyrics,
      key: chart.key,
      year_published: chart.year_published,
      published: chart.published,
      raw: chart.raw,
      chords_old: chart.chords_old,
      lyrics_old: chart.lyrics_old,
    }
  )
}

const getCreatableChart = (chart: Chart): CreateableChart => {
  const { chords_old, lyrics_old, raw, ...creatableChart } = getUpdatableChart(chart);
  return creatableChart;
}

export default function ChartSubmitButtons() {
  const navigate = useNavigate()
  const { editedChart, setEditedChart } = useChartEditContext();

  const updateChartQuery = useUpdateChart();
  const createChartQuery = useCreateChart();
  const destroyChartQuery = useDestroyChart();


  function submit(action: SubmitAction) {
  }

  const handleSave = async () => {
    try {
      await updateChartQuery.mutateAsync({ id: editedChart.id!, updatableChart: getUpdatableChart(editedChart) })
      reactQueryClient.invalidateQueries({ queryKey: ['charts'] });
      reactQueryClient.invalidateQueries({ queryKey: ['charts', editedChart.id] });
      notify('Chart updated successfully', 'success')
    } catch (error) {
      notify('Error updating chart', 'error')
      console.error('Error updating chart:', error);
    }
  }

  const handleCreate = async () => {
    try {
      const createdChart = await createChartQuery.mutateAsync({ chartNew: getCreatableChart(editedChart) })
      reactQueryClient.invalidateQueries({ queryKey: ['charts'] });
      notify('Chart created successfully', 'success')
      navigate(chartEditPath(createdChart.id))
    } catch (error) {
      notify('Error creating chart', 'error')
      console.error('Error creating chart:', error);
    }
  }

  const handleDestroy = async () => {
    if (!window.confirm('Are you sure you want to delete this chart? This action cannot be undone.')) {
      return;
    }

    if (!editedChart.id) return;
    try {
      await destroyChartQuery.mutateAsync({ id: editedChart.id })
      reactQueryClient.invalidateQueries({ queryKey: ['charts'] });
      notify('Chart deleted successfully', 'success')
      navigate('/')
    } catch (error) {
      notify('Error deleting chart', 'error')
      console.error('Error deleting chart', error);
    }
  }

  return (
    <div className="flex flex-col gap-2 items-end">
      <div className="flex items-center gap-2">
        <input
          type="checkbox"
          onChange={e => setEditedChart(draft => { draft.published = e.target.checked })}
          name="published"
          checked={editedChart.published}
        />
        <span>publish (findable in search)</span>
      </div>

      <div className="flex items-center gap-3">
        {editedChart.id ? (
          <>
            <Button size="xs" variant="contained" color="primary" startIcon={<FaFloppyDisk />} onClick={handleSave}>
              Save
            </Button>
            <Button size="xs" variant="contained" color="error" startIcon={<FaTrash />} onClick={handleDestroy}>
              Delete
            </Button>
            {/* <Button size="xs" variant="contained" color="warning" startIcon={<FaCopy />} onClick={() => submit('duplicate')}>
              Duplicate
            </Button> */}
            <Button size="xs" variant="outlined" color="inherit" startIcon={<FaXmark />} component={Link} to={chartShowPath(editedChart.id)}>
              Cancel
            </Button>
          </>
        ) : (
          <Button size="xs" variant="contained" color="primary" startIcon={<FaFloppyDisk />} onClick={handleCreate}>
            Create
          </Button>
        )}
      </div>
    </div>
  )
}

